import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BRANDS } from "@/i18n/translations";
import { Reveal, KineticLines, Stagger, staggerItem } from "@/components/motion/Reveal";
import CertBar from "@/components/CertBar";
import EditorialMarquee from "@/components/EditorialMarquee";
import SmartImg from "@/components/SmartImg";

const HERO_IMG = "/media/home-hero.png";
const BAKING_IMG = "/media/snapshot.png";
const QUALITY_IMG = "/media/quality.png";
const COCOA_IMG = "/media/boncoco-bg.png";

const MANIFESTO = [
  { n: "01", t: { id: "Konsistensi Batch-ke-Batch", en: "Batch-to-Batch Consistency" }, d: { id: "Kontrol mutu ketat memastikan performa yang sama di setiap produksi.", en: "Rigorous quality control ensures the same performance in every production run." } },
  { n: "02", t: { id: "Katalog yang Lengkap", en: "A Complete Catalogue" }, d: { id: "Dari gula ritel hingga cokelat bubuk ekspor — satu mitra untuk semua kebutuhan.", en: "From retail sugar to export cocoa powder — one partner for every need." } },
  { n: "03", t: { id: "Layanan yang Responsif", en: "Responsive Service" }, d: { id: "Tim yang siap merespons spesifikasi, sampel, dan volume Anda dengan cepat.", en: "A team ready to respond to your specification, samples and volume, fast." } },
  { n: "04", t: { id: "Warisan Sejak 1976", en: "Heritage Since 1976" }, d: { id: "Hampir lima dekade keahlian meracik bahan kue Indonesia.", en: "Nearly five decades of expertise crafting Indonesian baking ingredients." } },
];

const SNAPSHOT = [
  { k: "1976", v: { id: "Tahun Berdiri", en: "Year Founded" } },
  { k: "5", v: { id: "Merek Rumah", en: "House Brands" } },
  { k: "Bekasi", v: { id: "Fasilitas Produksi", en: "Manufacturing Facility" } },
  { k: "Global", v: { id: "Jangkauan Ekspor", en: "Export Reach" } },
];

const TESTIMONIALS = [];

export default function Home() {
  const { t, loc } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden">
        <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0">
          <SmartImg src={HERO_IMG} alt="Manufacturing facility" priority className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/45 to-ink/30" />
        </motion.div>

        <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="flex items-center gap-3 mb-6 text-cream/80">
            <span className="h-px w-10 bg-clara-accent" />
            <span className="text-[11px] md:text-xs uppercase tracking-[0.3em]">{t("home.heroKicker")}</span>
          </motion.div>

          <h1 className="font-serif text-cream text-[19vw] sm:text-[15vw] md:text-[12vw] lg:text-[11rem] leading-[0.86] tracking-tight">
            <KineticLines lines={[t("home.heroLine1"), t("home.heroLine2")]} delay={0.2} stagger={0.14} />
          </h1>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
            <p className="text-cream/80 max-w-xl text-base md:text-lg leading-relaxed">{t("home.heroSub")}</p>
            <div className="flex flex-wrap gap-3 shrink-0">
              <Link to="/contact" data-testid="hero-quote-btn" className="group inline-flex items-center gap-2 bg-cream text-ink rounded-full pl-7 pr-6 py-4 text-sm font-semibold hover:bg-claris hover:text-cream transition-colors duration-300">
                {t("nav.requestQuote")} <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href="#brands" data-testid="hero-brands-btn" className="inline-flex items-center gap-2 border border-cream/40 text-cream rounded-full px-7 py-4 text-sm font-semibold hover:bg-cream/10 transition-colors duration-300">
                {t("common.exploreBrands")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BRANDS */}
      <section id="brands" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-32">
        <Reveal className="max-w-2xl mb-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-inkmute font-semibold mb-4">{t("nav.brands")}</div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] text-ink">{t("home.brandsTitle")}</h2>
          <p className="text-inkmute mt-5 text-lg leading-relaxed">{t("home.brandsSub")}</p>
        </Reveal>

        <Stagger className="grid grid-cols-1 md:grid-cols-6 gap-5">
          {BRANDS.map((b, i) => (
            <motion.div key={b.slug} variants={staggerItem} className={i < 2 ? "md:col-span-3" : "md:col-span-2"}>
              <Link to={`/brands/${b.slug}`} data-testid={`home-brand-${b.slug}`} className="group block h-full bg-white border border-line hover:border-ink transition-colors duration-300 p-8 md:p-10">
                <div className="flex items-start justify-between">
                  {b.logo ? (
                    <SmartImg src={b.logo} alt={b.name} className="h-16 md:h-20 max-w-[180px] object-contain object-left" />
                  ) : (
                    <span className="h-1.5 w-14 group-hover:w-24 transition-all duration-500 mt-3" style={{ background: b.accent }} />
                  )}
                  <ArrowUpRight size={20} className="text-inkmute group-hover:text-claris transition-colors" />
                </div>
                <div className="mt-8">
                  <div className="font-serif text-4xl md:text-5xl text-ink">{b.name}</div>
                  <div className="text-[11px] uppercase tracking-widest text-inkmute mt-2">{loc(b.tagline)}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </Stagger>
      </section>

      <EditorialMarquee text="Tan Putra Tama" />

      {/* TRUST / CERT — Quality you can trust */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative overflow-hidden aspect-[4/5]">
              <SmartImg src={QUALITY_IMG} alt="Quality you can trust" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="text-[11px] uppercase tracking-[0.3em] text-claris font-semibold mb-4">{t("common.since")}</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-ink mb-8">
                {loc({ id: "Kualitas yang bisa Anda percaya, disertifikasi secara resmi.", en: "Quality you can trust, officially certified." })}
              </h2>
            </Reveal>
            <Reveal delay={0.15}><CertBar /></Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO / WHY */}
      <section className="bg-ink text-cream py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal className="max-w-xl mb-16">
            <div className="text-[11px] uppercase tracking-[0.3em] text-clara-accent font-semibold mb-4">{t("home.whyTitle")}</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02]">
              {loc({ id: "Empat alasan produsen memilih kami.", en: "Four reasons manufacturers choose us." })}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.08}>
                <div className="flex gap-6 md:gap-8 border-t border-cream/15 pt-8">
                  <span className="font-serif text-5xl md:text-6xl text-clara-accent/70 leading-none">{m.n}</span>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl">{loc(m.t)}</h3>
                    <p className="text-cream/60 mt-3 leading-relaxed">{loc(m.d)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SNAPSHOT + IMAGE */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="text-[11px] uppercase tracking-[0.3em] text-inkmute font-semibold mb-6">{t("home.snapshotTitle")}</div>
            <div className="grid grid-cols-2 gap-px bg-line border border-line">
              {SNAPSHOT.map((s) => (
                <div key={s.k} className="bg-cream px-6 py-10">
                  <div className="font-serif text-5xl md:text-6xl text-claris leading-none">{s.k}</div>
                  <div className="text-sm text-inkmute mt-3 uppercase tracking-wider">{loc(s.v)}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <div className="relative overflow-hidden aspect-[4/5]">
              <SmartImg src={BAKING_IMG} alt="Baking ingredients" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-clara-bg text-clara-text">
        <SmartImg src={COCOA_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 py-28 md:py-40 text-center">
          <Reveal>
            <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95]">{t("home.ctaTitle")}</h2>
            <p className="text-clara-text/70 max-w-2xl mx-auto mt-8 text-lg leading-relaxed">{t("home.ctaSub")}</p>
            <Link to="/contact" data-testid="home-cta-btn" className="group inline-flex items-center gap-2 bg-clara text-white rounded-full pl-8 pr-6 py-4 mt-10 text-sm font-semibold uppercase tracking-widest hover:bg-[#8A362B] transition-colors duration-300">
              {t("nav.requestQuote")} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
